import { useFormContext } from "react-hook-form";
import {
    checkout_form,
    checkout_form_error_strings,
} from "repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/CheckoutForm/CheckoutForm.constants";
import { useSelector } from "react-redux";
import { EMAIL_REGEX } from "repoV2/utils/common/validators/regex.constants";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import { IAppliedCouponCodeDetails } from "repoV2/features/Common/modules/FormData/interfaces/IAppliedCouponCodeDetails";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import { useCheckoutFormContext } from "repoV2/features/CheckoutPages/modules/CheckoutFormContextProvider";
import { validatePhoneNumber } from "repoV2/features/Common/modules/Form/utils/validators";
import { INDIA_COUNTRY_DATA } from "repoV2/utils/common/location/countriesData";
import { IExlySearchableDropdownOption } from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdown/ExlySearchableDropdown.interfaces";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { getShowAddressFields } from "repoV2/features/CheckoutPages/utils/getShowAddressFields";
import { getSystemTimezone } from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { DEFAULT_TIMEZONE } from "repoV2/features/Common/modules/Timezone/Timezone.constants";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { getEmailHasValidTopLevelDomain } from "repoV2/utils/common/validators/validators";
import {
    GST_STATUS,
    LISTING_TYPES,
} from "repoV2/features/Listing/Listing.constants";
import { useSetIsFileUploading } from "repoV2/features/CheckoutPages/utils/useCustomQuestionFileUploadingHelper";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import useGetBillingStateHelpers from "repoV2/features/CheckoutPages/utils/useGetBillingStateHelpers";
import { IUserDetails } from "repoV2/features/Common/modules/FormData/interfaces/UserDetailsFormGroup.interfaces";
import { single_value_count } from "repoV2/features/CheckoutPages/constants/CheckoutPages.constants";
import { useState } from "react";
import { useOnFirstInteraction } from "repoV2/features/Common/utils/useOnFirstInteraction";
import { useUserDetailsFormContext } from "./modules/UserDetailsFormContextProvider";
import {
    COUPON_TYPES,
    default_applied_coupon_code_details,
    loyalty_email,
} from "../CouponCodeFormGroup/CouponCodeFormGroup.constants";

const {
    user_details: { field_names, placeholders },
} = checkout_form;

const { required, invalid_email, phone_already_exists, email_already_exists } =
    checkout_form_error_strings;

/**
 * @returns The upload key on s3, or path to which the file upload will be done
 */
export const getCusomQuestionFileUploadPath = (listingUuid: string) =>
    `question-listings/${listingUuid}`;

export const useUserDetailsFormGroup = () => {
    const { getValues, setValue } = useFormContext();
    const { setFormData } = useSetFormData();
    const { isUserEmailMandatory, isBillingStateMandatory } =
        useCheckoutFormContext();
    const { listingUuid, listingType, disableCheckEmailTopLevelDomain } =
        useCheckoutBasicDetailsContext();
    const { setIsCustomQuestionFileUploading } = useSetIsFileUploading();
    const { isBillingStateDisabled } = useGetBillingStateHelpers();

    const emailPlaceholder = isUserEmailMandatory
        ? placeholders.email
        : placeholders.email_optional;

    const userDetails =
        useGetFormData<{ [userId: string]: IUserDetails }>({
            key: `${FORM_KEYS.user_details}`,
        }) || {};

    const priceDetails =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails[listingUuid]?.priceSection
                    ?.data
        ) || {};

    const appliedCouponCodeData =
        useGetFormData<IAppliedCouponCodeDetails>({
            key: `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
        }) || default_applied_coupon_code_details;

    const bookingQuestions = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.customQuestions
                ?.data?.booking_questions
    );

    const generateUploadKey = (file: File) =>
        `${getCusomQuestionFileUploadPath(
            listingUuid
        )}/${new Date().toISOString()}_${file.name}`;

    const { userId } = useUserDetailsFormContext();

    const showInternationalInputFields =
        priceDetails?.showInternationalInputFields ||
        getSystemTimezone() !== DEFAULT_TIMEZONE;

    const [isFirstInteractionDone, setIsFirstInteractionDone] = useState(false);
    useOnFirstInteraction({
        onFirstInteraction: () => setIsFirstInteractionDone(true),
    });
    const { showAddressInsideUserDetails } = getShowAddressFields({
        listingType,
        showInternationalInputFields,
    });

    const showPaymentMethodInputField =
        priceDetails?.showPaymentMethodInputField;

    const currencyName = priceDetails?.currency_name || "";

    const isMultipleUserExist =
        Object.keys(userDetails).length > single_value_count;

    let isUserEmailDisabled = false;

    // if the discount coupon is applied and the coupon is of loyalty type, then email is prefilled and we disable its editing as the code is linked to the email
    if (
        appliedCouponCodeData.applied &&
        appliedCouponCodeData?.couponType === COUPON_TYPES.LOYALTY_COUPON &&
        !isMultipleUserExist
    ) {
        isUserEmailDisabled = true;
    } else {
        isUserEmailDisabled = false;
    }

    const checkIsFieldValueExistForOtherUser = (
        fieldName: typeof field_names.email | typeof field_names.phone,
        value: string
    ) => {
        return Object.keys(userDetails).some(currentUserId => {
            // Check that the user we are verifying is not the
            // user for which data is being filled in this instance
            if (userId !== currentUserId) {
                // Check if the value already exists for a previous user
                return userDetails[currentUserId][fieldName] === value;
            }
            return false;
        });
    };
    const gstState = priceDetails?.gst_state;

    // incase of merchandise, we show the state field as a part of address details,
    // so no point of asking the value again
    // also, for international users or non gst creators, no point of asking billing state
    const showGstBillingStateField =
        listingType !== LISTING_TYPES.MERCHANDISE &&
        !showInternationalInputFields &&
        gstState !== GST_STATUS.INAPPLICABLE;

    const phoneNumberValidator = () => {
        const data = getValues();
        const phoneFieldName = getUserFormFieldName(userId, field_names.phone);
        const countryCodeFieldName = getUserFormFieldName(
            userId,
            field_names.country_code
        );
        const isInIndia =
            data[countryCodeFieldName] === INDIA_COUNTRY_DATA.dial_code;
        const phoneNumber = data[phoneFieldName];

        const isPhoneAlreadyExist = checkIsFieldValueExistForOtherUser(
            field_names.phone,
            phoneNumber
        );
        if (!checkIsStringEmpty(phoneNumber) && isPhoneAlreadyExist) {
            return phone_already_exists;
        }
        return validatePhoneNumber({ phoneNumber, isInIndia });
    };

    const emailValidator = () => {
        const data = getValues();
        const fieldNameWithId = getUserFormFieldName(userId, field_names.email);
        const email: string = data[fieldNameWithId];

        const isEmailAlreadyExists = checkIsFieldValueExistForOtherUser(
            field_names.email,
            email
        );
        // @dev
        // check email value if empty only when isUserEmailMandatory(comes from admin panel) is true
        // if fails => Required is shown as error text
        if (isUserEmailMandatory && checkIsStringEmpty(email)) return required;
        // @dev
        // check email syntax if not empty email value
        // 1. checks for email regex
        // 2. check if we need to validate the email top level domain
        // if so, then checks for valid ending domain
        // if fails => Invalid email shown as error text
        // 3. checks for whether user with the email already exists
        else if (
            !checkIsStringEmpty(email) &&
            (!EMAIL_REGEX.test(email) ||
                (!disableCheckEmailTopLevelDomain &&
                    !getEmailHasValidTopLevelDomain(email)))
        )
            return invalid_email;
        else if (!checkIsStringEmpty(email) && isEmailAlreadyExists) {
            return email_already_exists;
        } else return true;
    };

    const billingStateValidator = () => {
        const data = getValues();
        const fieldNameWithId = getUserFormFieldName(
            userId,
            field_names.billing_state
        );
        const billingState: IExlySearchableDropdownOption =
            data[fieldNameWithId] || "";

        if (isBillingStateMandatory && checkIsStringEmpty(billingState?.value))
            return required;
        else return true;
    };

    const setUserDetailsFormData = ({
        fieldName,
        value,
    }: {
        fieldName: string;
        value?: string | string[] | Date;
    }) => {
        const data = getValues();
        const fieldNameWithId = getUserFormFieldName(userId, fieldName);
        setFormData(
            `${FORM_KEYS.user_details}.${userId}.${fieldName}`,
            value || data[fieldNameWithId]
        );
        if (fieldName === field_names.email) {
            const preFilledLoyaltyEmailValue =
                data[getUserFormFieldName(userId, field_names.email)];
            setValue(loyalty_email, preFilledLoyaltyEmailValue);
        }
    };

    const setBillingStateFormData = ({
        fieldName,
        value,
    }: {
        fieldName: string;
        value?: IExlySearchableDropdownOption;
    }) => {
        const data = getValues();
        const fieldNameWithId = getUserFormFieldName(userId, fieldName);

        setFormData(
            `${FORM_KEYS.user_details}.${userId}.${fieldName}`,
            value || data[fieldNameWithId]
        );
    };

    // File uploader
    const setFileUploaderFileValue = ({
        fieldName,
        fileData,
        value,
    }: {
        fieldName: string;
        fileData: {
            type: string;
            value: string;
            thumbnail?: string;
        };
        value: string;
    }) => {
        const fieldNameWithId = getUserFormFieldName(userId, fieldName);

        setValue(fieldNameWithId, value);
        setValue(`${fieldNameWithId}-file`, fileData);
        setFormData(`${FORM_KEYS.user_details}.${userId}.${fieldName}`, value);
    };

    // Clear file upload value
    const clearFileUploaderFileValue = ({
        fieldName,
    }: {
        fieldName: string;
    }) => {
        const fieldNameWithId = getUserFormFieldName(userId, fieldName);
        setValue(fieldNameWithId, null);
        setValue(`${fieldNameWithId}-file`, null);
        setFormData(`${FORM_KEYS.user_details}.${userId}.${fieldName}`, null);
    };

    const generateBookingQuestionFieldName = (questionUuid: string) => {
        return getUserFormFieldName(userId, questionUuid);
    };

    return {
        emailPlaceholder,
        emailValidator,
        phoneNumberValidator,
        isBillingStateMandatory,
        showGstBillingStateField,
        billingStateValidator,
        setUserDetailsFormData,

        // Dont show address fields until user has interacted with the page
        // Why?: Address fields appearing on mount reduces LCP.
        showAddressInsideUserDetails: isFirstInteractionDone
            ? showAddressInsideUserDetails
            : false,

        showInternationalInputFields,
        showPaymentMethodInputField,
        bookingQuestions,
        setFileUploaderFileValue,
        clearFileUploaderFileValue,
        generateBookingQuestionFieldName,
        generateUploadKey,
        setIsCustomQuestionFileUploading,
        setBillingStateFormData,
        isBillingStateDisabled,
        isUserEmailDisabled,
        currencyName,
    };
};

export const getUserFormFieldName = (userId: string, fieldBaseName: string) => {
    return `${userId}_${fieldBaseName}`;
};

export const validators = {
    [field_names.name]: (value: string) => {
        const _value = (value && value.trim()) || "";
        return checkIsStringEmpty(_value) ? required : true;
    },
};
